
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Auth from '../../pages/Auth/auth'
import { useDispatch, useSelector } from 'react-redux'
import AuthLoader from '../Loader/AuthLoader'
import { pages } from './pages'
import Sidebar from '../Sidebar/Sidebar'
import Navbar from '../Navbar/Navbar'
import PopupManager from '../PopupManager/PopupManager'
import { getAllOperations, stopOperationsListener } from '../../../Redux/operations/operationsSlice'
import { getAllAppointments, stopAppointmentsListener } from '../../../Redux/appointments/appointmentSlice'
import { getAllPatients, stopPatientsListener } from '../../../Redux/patients/patientsSlice'
import { getAllExpenditures, stopExpensesListener } from '../../../Redux/expenses/expensesSlice'
import { Helmet } from 'react-helmet-async'
import { getAllWithdraws, stopWithdrawsListener } from '../../../Redux/withdraws/withdrawsSlice'
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton'

const isDev = process.env.NODE_ENV === 'development'
let firestoreListenersInitialized = false

const index = () => {
  const auth = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const { loading, user, error } = auth
  const listenersInitialized = useRef(false)
  const loadingStartTime = useRef(null)
  const [showSlowConnectionWarning, setShowSlowConnectionWarning] = useState(false)
  
  // Get loading states from all slices
  const patientsLoading = useSelector((state) => state.patients.loading)
  const appointmentsLoading = useSelector((state) => state.appointments.loading)
  const operationsLoading = useSelector((state) => state.operations.loading)
  const expensesLoading = useSelector((state) => state.expenses.loading)
  const withdrawsLoading = useSelector((state) => state.withdraws.loading)
  
  // Check if any data is currently loading
  const isDataLoading = patientsLoading || appointmentsLoading || operationsLoading || expensesLoading || withdrawsLoading
  
  // Calculate loading progress
  const totalModules = 5
  const loadedModules = [
    !operationsLoading,
    !withdrawsLoading, 
    !expensesLoading,
    !appointmentsLoading,
    !patientsLoading
  ].filter(Boolean).length
  const loadingProgress = (loadedModules / totalModules) * 100

  useEffect(() => {
    // Only initialize listeners once when user is authenticated
    if (user?.user && user?.profile && !error && !listenersInitialized.current && !firestoreListenersInitialized) {
      listenersInitialized.current = true
      firestoreListenersInitialized = true
      
      // OPTIMIZED LOADING: Start with most critical data first, then stagger the rest
      loadingStartTime.current = performance.now()
      if (isDev) {
        console.log('🚀 Starting optimized data loading with progressive enhancement...')
        console.log('🌐 Firestore connection status: Initializing...')
      }
      
      // NETWORK DIAGNOSTICS: Check connection quality
      const connectionStart = performance.now()
      if (isDev) {
        console.log('🩺 Running network diagnostics...')
        console.log(`📍 User Agent: ${navigator.userAgent}`)
        console.log(`🌐 Connection: ${navigator.connection?.effectiveType || 'unknown'}`)
        console.log(`📶 Downlink: ${navigator.connection?.downlink || 'unknown'} Mbps`)
        console.log(`⚡ RTT: ${navigator.connection?.rtt || 'unknown'}ms`)
      }
      
      // CONNECTION TIMEOUT: Set a timeout to detect slow connections
      const connectionTimeout = setTimeout(() => {
        if (isDev) {
          console.warn('⚠️ Firestore connection is taking longer than expected (>5s)')
          console.warn('💡 This may be due to network issues or Firestore server latency')
        }
        setShowSlowConnectionWarning(true)
      }, 5000)
      
      // Phase 1: Load critical operational data first (small collections)
      console.log('⚡ Loading operations (critical)...')
      
      // RETRY MECHANISM: Implement exponential backoff for failed connections
      const retryWithBackoff = async (fn, retries = 3, delay = 1000) => {
        try {
          return await fn()
        } catch (error) {
          if (retries > 0) {
            console.log(`🔄 Retrying connection in ${delay}ms... (${retries} attempts left)`)
            await new Promise(resolve => setTimeout(resolve, delay))
            return retryWithBackoff(fn, retries - 1, delay * 2)
          }
          throw error
        }
      }
      
      retryWithBackoff(() => dispatch(getAllOperations())).then(() => {
        const connectionTime = performance.now() - connectionStart
        clearTimeout(connectionTimeout)
        setShowSlowConnectionWarning(false)
        console.log(`🌐 Firestore connection established successfully in ${Math.round(connectionTime)}ms`)
        
        // Adjust loading strategy based on connection speed
        if (connectionTime > 3000) {
          console.log('🐌 Slow connection detected - using conservative loading strategy')
          // Increase delays for slow connections
        } else {
          console.log('🚀 Fast connection detected - using optimal loading strategy')
        }
      }).catch((error) => {
        clearTimeout(connectionTimeout)
        console.error('❌ Firestore connection failed:', error)
        setShowSlowConnectionWarning(true)
      })
      
      // Phase 2: Load financial data (needed for dashboard)
      setTimeout(() => {
        if (isDev) {
          console.log('💰 Loading financial data (high priority)...')
        }
        dispatch(getAllWithdraws())
        dispatch(getAllExpenditures())
      }, 150)
      
      // Phase 3: Load appointments (frequently accessed)
      setTimeout(() => {
        if (isDev) {
          console.log('📋 Loading appointments (medium priority)...')
        }
        dispatch(getAllAppointments())
      }, 400)
      
      // Phase 4: Load patients last (largest dataset, lower immediate priority)
      setTimeout(() => {
        if (isDev) {
          console.log('👥 Loading patients (background loading)...')
        }
        dispatch(getAllPatients())
      }, 800)
    }
    
    // Reset listeners when user logs out
    if (!user?.user && listenersInitialized.current) {
      listenersInitialized.current = false
      firestoreListenersInitialized = false
      
      // Stop all listeners and reset Redux states
      dispatch(stopPatientsListener())
      dispatch(stopAppointmentsListener())
      dispatch(stopOperationsListener())
      dispatch(stopWithdrawsListener())
      dispatch(stopExpensesListener())
      dispatch({ type: 'patients/resetPatients' })
      dispatch({ type: 'appointments/resetAppointments' })
      dispatch({ type: 'operations/resetOperations' })
      dispatch({ type: 'withdraws/resetWithdraws' })
      dispatch({ type: 'expenses/resetExpenses' })
    }
  }, [user?.user?.uid, user?.profile?.id, error, dispatch]) // Re-add auth dependencies

  // Track loading completion and performance
  useEffect(() => {
    if (!isDataLoading && loadingStartTime.current) {
      const loadingTime = performance.now() - loadingStartTime.current
      if (isDev) {
        console.log(`🎉 ALL DATA FULLY LOADED in ${Math.round(loadingTime)}ms (${(loadingTime / 1000).toFixed(2)}s)`)
        console.log(`📊 Loading phases completed:
          ⚡ Operations: Critical data ✅
          💰 Financial: High priority ✅  
          📋 Appointments: Medium priority ✅
          👥 Patients: Background loading ✅`)
      }
      loadingStartTime.current = null
    }
  }, [isDataLoading])
  const { profile } = user || {}
  const isAdmin = profile?.role === 'admin'

  if (loading) {
    return <AuthLoader />
  }

  if (!user?.user || !user?.profile || error) {
    return <Auth />
  }

  if (isDev) {
    console.log(user, error)
  }
  /**
   * Recursively return child routes for a route in a list of routes
   * @param  {[pathnames]} arg1 An array of routes to loop through
   * @param  {prefix} arg2 A prefix to add onto the previous route path
   */

  const routeHandler = (pagenames, prefix = '') => {
    return pagenames.map((route) => {
      if (!route.children) {
        if (route.path === 'admin/*' && !isAdmin) {
          return (
            <Route
              key={`${prefix}${route.path}`}
              path={`${prefix}${route.path}`}
              element={<Navigate to="/management/dashboard" replace />}
            />
          )
        }
        return (
          <Route
            key={`${prefix}${route.path}`}
            path={`${prefix}${route.path}`}
            exact={route.exact}
            element={
              <route.layout>
                <Helmet>
                  <title>{route.title}</title>
                </Helmet>
                <route.element />
              </route.layout>
            }
          />
        )
      }
      return routeHandler(route.children, route.path)
    })
  }

  return (
    <>
      <Navbar />
      <Sidebar />
      <PopupManager />
      
      {/* Slow connection warning */}
      {showSlowConnectionWarning && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '12px',
          zIndex: 9998,
          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          fontSize: '14px',
          fontWeight: 'bold',
          minWidth: '300px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px' }}>⚠️ Lidhja është e ngadaltë</div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            Firestore po merr kohë për t&apos;u lidhur.<br />
            Mund të jetë problem i internetit ose serverit.
          </div>
        </div>
      )}

      {/* Enhanced loading indicator with real progress tracking */}
      {isDataLoading && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '12px',
          zIndex: 9999,
          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          fontSize: '14px',
          fontWeight: 'bold',
          minWidth: '280px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px' }}>
            🔄 Duke ngarkuar të dhënat... {Math.round(loadingProgress)}%
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px' }}>
            {operationsLoading ? '⚡ Operacionet' : '✅ Operacionet'} • {' '}
            {withdrawsLoading ? '💰 Arkën' : '✅ Arkën'} • {' '}
            {expensesLoading ? '💸 Shpenzimet' : '✅ Shpenzimet'}
            <br />
            {appointmentsLoading ? '📋 Terminet' : '✅ Terminet'} • {' '}
            {patientsLoading ? '👥 Pacientët' : '✅ Pacientët'}
          </div>
          <div style={{ 
            marginTop: '8px', 
            height: '6px', 
            backgroundColor: 'rgba(255,255,255,0.3)', 
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              backgroundColor: 'white',
              width: `${loadingProgress}%`,
              transition: 'width 0.3s ease',
              borderRadius: '3px'
            }}></div>
          </div>
          <div style={{ fontSize: '10px', marginTop: '5px', opacity: 0.8 }}>
            {loadedModules}/{totalModules} module{loadedModules !== 1 ? 't' : ''} loaded
          </div>
        </div>
      )}
      
      {/* Show loading skeleton during initial load */}
      {isDataLoading && loadingProgress < 40 ? (
        <LoadingSkeleton />
      ) : (
        <Routes>
          <Route path='/' element={<Navigate to={isAdmin ? '/management/dashboard' : '/management'} replace />} />
          <Route path='*' element={<Navigate to={isAdmin ? '/management/dashboard' : '/management'} replace />} />
          {routeHandler(pages)}
        </Routes>
      )}
    </>
  )
}

export default index
